import { useEffect, useRef, useState } from 'react';
import { Environment } from '~/engine/data/Environment';
import {
    domainEventBus,
    ROI_CONTOUR_EDIT_EVENT_TYPES,
    RoiContourEditEvent,
} from '~/domain/events';
import type { RoiId } from '~/domain/changes/ids';

/** Reference-stable comparison, so an unchanged sample re-renders nothing. */
function sameVersions(
    a: ReadonlyMap<RoiId, number>,
    b: ReadonlyMap<RoiId, number>
): boolean {
    if (a.size !== b.size) {
        return false;
    }
    for (const [roiId, version] of a) {
        if (b.get(roiId) !== version) {
            return false;
        }
    }
    return true;
}

/** The contour version of each of `roiIds` in `environment` now. */
function sampleVersions(
    environment: Environment | null,
    roiIds: Iterable<RoiId>
): Map<RoiId, number> {
    const versions = new Map<RoiId, number>();
    for (const roiId of roiIds) {
        versions.set(
            roiId,
            environment?.structureSet?.getRoi(roiId)?.sdfVersion ?? 0
        );
    }
    return versions;
}

/**
 * Current contour version of each of `roiIds`, re-sampled whenever one
 * of them is edited. Missing or not-yet-loaded structures read 0.
 *
 * The version is {@link RegionOfInterest.sdfVersion} — the ROI-level
 * counter kept for exactly this purpose — and NOT the SDF's own
 * `dataVersion`: a replaced texture carries a fresh field whose
 * `dataVersion` restarts at 0, so a reader keyed on it can watch geometry
 * change from version 3 to version 0 and, worse, see an edited structure
 * report the same 0 it reported before (an ABA collision). `sdfVersion`
 * is monotonic across texture swaps.
 *
 * Sampling alone is not enough to keep it current: contour edits mutate
 * the SDF in place and touch no React state, so a consumer that only
 * re-reads on render can hold a version from before an edit indefinitely.
 * The three contour-edit events are therefore observed — strokes publish
 * `RoiContoursModifiedEvent`, while undo and redo restore the pixels
 * through their own events WITHOUT re-emitting it, so watching the first
 * alone goes stale on an undo (see {@link RoiContourEditEvent}).
 *
 * @param environment Engine environment holding the live structure set.
 * @param roiIds ROIs to watch. Order is irrelevant; an empty list
 *                   subscribes to nothing.
 */
export function useRoiSdfVersions(
    environment: Environment | null,
    roiIds: readonly RoiId[]
): ReadonlyMap<RoiId, number> {
    // Sampled on the first render too, so a consumer keyed on the
    // versions does not see a change from nothing to the first sample.
    const [versions, setVersions] = useState<ReadonlyMap<RoiId, number>>(() =>
        sampleVersions(environment, roiIds)
    );

    // Read through a ref so a fresh array of the SAME ids does not
    // resubscribe on every render; the key below decides when it must.
    const roiIdsRef = useRef(roiIds);
    roiIdsRef.current = roiIds;
    const watchedKey = [...roiIds].sort().join(',');

    useEffect(() => {
        const watched = new Set(roiIdsRef.current);
        const sample = () => {
            const next = sampleVersions(environment, watched);
            setVersions((previous) =>
                sameVersions(previous, next) ? previous : next
            );
        };
        sample();

        const onContourEdit = (event: RoiContourEditEvent) => {
            if (watched.has(event.roiId)) {
                sample();
            }
        };
        const subscriptions = ROI_CONTOUR_EDIT_EVENT_TYPES.map((eventType) =>
            domainEventBus.subscribe(eventType, onContourEdit)
        );
        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, [environment, watchedKey]);

    return versions;
}
